import {
  ScrollArea,
  Table,
  Checkbox,
  Badge,
  Divider,
  useMantineTheme,
  Text,
  Flex,
  Stack,
} from "@mantine/core";
import { Link } from "react-router-dom";

export const internalColumns = [
  {
    name: "__id",
    label: "ID",
    type: "TEXT",
  },
  {
    name: "__runid",
    label: "Run ID",
    type: "TEXT",
  },
  {
    name: "__runseq",
    label: "Run Seq",
    type: "TEXT",
  },
  {
    name: "__matchids",
    label: "Match IDs [ __matchids ]",
    type: "TEXT",
  },
  {
    name: "__comment",
    label: "Comment",
    type: "TEXT",
  },
  {
    name: "__externalid",
    label: "External ID",
    type: "TEXT",
  },
  {
    name: "details",
    label: "Details",
    type: "TEXT",
  },
];

function genLink(value: string, data: any) {
  let to = "";
  const linkTo: { name: string; value: string } = data.props.find(
    (prop: { name: string; value: string }) => prop.name === "__to"
  );

  if (linkTo.value) {
    to = linkTo.value;
    to = to.replaceAll("${__value}", value);
    to = to.replaceAll("${__id}", data.rowData["__id"]);
  }

  return to;
}

function Links({ data }: { data: any }) {
  const theme = useMantineTheme();

  const seperator: { name: string; value: string } = data.props.find(
    (prop: { name: string; value: string }) => prop.name === "__seperator"
  );

  const displayValue: { name: string; value: string } = data.props.find(
    (prop: { name: string; value: string }) => prop.name === "__displayValue"
  );
  if (displayValue.value) {
    return (
      <Stack gap="0">
        <Link style={{ marginRight: theme.spacing.xxs }} to={genLink("", data)}>
          <Text size={"xs"}>{displayValue.value}</Text>
        </Link>
      </Stack>
    );
  } else {
    if (!data.value) {
      return <></>;
    }
    const links = data.value.split(seperator ? seperator.value : ",");

    if (links && links.length > 0) {
      return (
        <Stack gap="0">
          {links.map((link: string, index: number) => {
            return (
              <Link
                key={index}
                style={{ marginRight: theme.spacing.xxs }}
                to={genLink(link, data)}
              >
                <Text size={"xs"}>{link}</Text>
              </Link>
            );
          })}
        </Stack>
      );
    } else {
      return <></>;
    }
  }
}

function getDataHeaders(
  table: string,
  data: any[],
  definition:
    | {
        name: string;
        type: string;
        label?: string;
      }[]
    | [],
  ignoreColumns: string[]
) {
  const headers: string[] = [];

  if (data && data.length > 0) {
    const firstRow = data[0];
    Object.keys(firstRow)
      .filter((key) => ignoreColumns.indexOf(key) === -1)
      .forEach((key) => {
        const column = definition.find((def) => {
          return def.name === key;
        });
        if (column) {
          headers.push(
            column.label
              ? `${column.label ? column.label : column.name} [ ${column.name} ]`
              : column.name
          );
        } else {
          const column = internalColumns.find((def) => {
            return def.name === key;
          });
          if (column) {
            headers.push(`${column.label ? column.label : column.name}`);
          } else {
            headers.push(key);
          }
        }
      });
  }
  return headers;
}

// TODO: Client side pagination
// TODO: Details modal
// TODO: Timestamp display

export default function DataDisplay({
  table,
  data,
  definition,
  links,
  enableSelection = false,
  selection = [],
  toggleRow,
  toggleAll,
  ignoreColumns = [],
  displayInModal = false,
  height = 250,
}: {
  table: string;
  data: any[];
  definition:
    | {
        name: string;
        type: string;
        label?: string;
      }[]
    | [];
  links: {
    name: string;
    split?: boolean;
    seperator?: string;
    displayValue?: string;
    link: string;
  }[];
  enableSelection?: boolean;
  selection?: string[];
  toggleRow?: (id: string) => void;
  toggleAll?: () => void;
  ignoreColumns?: string[];
  displayInModal?: boolean;
  height?: number;
}) {
  const theme = useMantineTheme();
  const headers = getDataHeaders(table, data, definition, ignoreColumns);
  if (data && data.length > 0) {
    return (
      <ScrollArea
        type="auto"
        style={{ height: height }}
        offsetScrollbars
        scrollbarSize={16}
      >
        <Table
          verticalSpacing="xs"
          fs="xs"
          horizontalSpacing={"xs"}
          style={{ marginBottom: theme.spacing.xs }}
        >
          <Table.Thead>
            <Table.Tr>
              {enableSelection ? (
                <Table.Th key={"__checkbox"}>
                  <Checkbox
                    // onChange={toggleAll}
                    onChange={toggleAll}
                    checked={selection.length === data.length}
                    indeterminate={
                      selection.length > 0 && selection.length !== data.length
                    }
                  />
                </Table.Th>
              ) : (
                <></>
              )}
              {headers.map((header) => {
                return (
                  <Table.Th
                    key={header}
                    style={{
                      minWidth: `${
                        displayInModal
                          ? Math.max(header.length * 7, 150)
                          : header.length * 10
                      }px`,
                    }}
                  >
                    <Text size="xs" fw={500}>
                      {header}
                    </Text>
                  </Table.Th>
                );
              })}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(data || []).map((rowData, index) => {
              return (
                <Table.Tr key={index}>
                  {enableSelection ? (
                    <Table.Td key={"__checkbox"}>
                      <Checkbox
                        checked={selection.includes(rowData.__id)}
                        onChange={() =>
                          toggleRow ? toggleRow(rowData.__id) : ""
                        }
                      />
                    </Table.Td>
                  ) : (
                    <></>
                  )}
                  {Object.keys(rowData)
                    .filter((key) => ignoreColumns.indexOf(key) === -1)
                    .map((key) => {
                      const columnLink = links.find(
                        (link) => link.name === key
                      );
                      if (columnLink) {
                        return (
                          <Table.Td key={key}>
                            <Links
                              data={{
                                rowData: rowData,
                                value: rowData[key],
                                props: [
                                  { name: "__to", value: columnLink.link },
                                  {
                                    name: "__displayValue",
                                    value: columnLink.displayValue,
                                  },
                                  {
                                    name: "__seperator",
                                    value: columnLink.seperator,
                                  },
                                ],
                              }}
                            />
                          </Table.Td>
                        );
                      }
                      return (
                        <Table.Td key={key}>
                          <Text size="xs">{rowData[key]}</Text>
                        </Table.Td>
                      );
                    })}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    );
  } else {
    return <Divider label="No data" labelPosition="left" />;
  }
}
